"use client";

import React from "react";
import { useController, Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { cn } from "../../../lib/src/utils";

interface NumberInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

export function NumberInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  placeholder,
  description,
  helperText,
  errorText,
  min,
  max,
  step,
  className,
  required = false,
  disabled = false,
}: NumberInputProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const hasError = error || errorText;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      field.onChange(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        field.onChange(numValue);
      }
    }
  };

  return (
    <FormItem className={cn("space-y-2", className)}>
      <FormLabel className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}>
        {label}
      </FormLabel>
      <FormControl>
        <Input
          {...field}
          type="number"
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={field.value === null || field.value === undefined ? "" : field.value}
          onChange={handleChange}
          className={cn(
            hasError && "border-destructive focus-visible:ring-destructive",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        />
      </FormControl>
      {description && <FormDescription className="text-sm text-muted-foreground">{description}</FormDescription>}
      {helperText && !hasError && <FormDescription className="text-sm text-muted-foreground">{helperText}</FormDescription>}
      {hasError && <FormMessage>{errorText || error?.message}</FormMessage>}
    </FormItem>
  );
}
